import { useEffect, useRef } from 'react';
import { getVersionAPI } from '../api/version.api';

const POLL_INTERVAL_MS = 120 * 1000;
const BANNER_ID = 'version-update-banner';
const FORCE_RELOAD_ID = 'version-force-reload-notice';
const FORCE_RELOAD_COUNTDOWN_S = 10;

const semverLt = (a, b) => {
  const [aMaj, aMin, aPat] = a.split('.').map(Number);
  const [bMaj, bMin, bPat] = b.split('.').map(Number);
  if (aMaj !== bMaj) return aMaj < bMaj;
  if (aMin !== bMin) return aMin < bMin;
  return aPat < bPat;
};

// GET /version (DE_CORE) now returns both the BE-required minimumClientVersion
// and the latest FE version pushed to it by the deploy pipeline, so a single
// authenticated request covers both Check A (soft banner) and Check B
// (force reload). See RELEASE_SOP.md for the backend contract.
const fetchVersionInfo = async () => {
  const data = await getVersionAPI();

  return {
    minimumClientVersion: data?.minimumClientVersion || null,
    feVersion: data?.feVersion || null
  };
};

const showForceReloadNotice = () => {
  if (document.getElementById(FORCE_RELOAD_ID)) return;

  const style = document.createElement('style');
  style.id = `${FORCE_RELOAD_ID}-style`;
  style.textContent = `
    @keyframes forceReloadSlideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes forceReloadPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
  `;
  document.head.appendChild(style);

  const notice = document.createElement('div');
  notice.id = FORCE_RELOAD_ID;
  notice.style.cssText = `
    position: fixed; bottom: 24px; right: 24px; z-index: 999999;
    background: #ffffff; color: #1a1a1a; padding: 16px 18px;
    border-radius: 12px; box-shadow: 0 4px 24px rgba(0,0,0,0.18);
    font-family: 'Roboto', sans-serif; font-size: 14px;
    display: flex; flex-direction: column; gap: 10px;
    min-width: 280px; max-width: 340px;
    border-left: 4px solid #d32f2f;
    animation: forceReloadSlideIn 0.3s ease-out;
  `;

  notice.innerHTML = `
    <div style="display: flex; flex-direction: column; gap: 3px;">
      <span style="font-weight: 700; font-size: 14px; color: #d32f2f;">Critical Update Required</span>
      <span style="font-size: 13px; color: #555; line-height: 1.4;">
        A critical system update has been applied. The page will automatically reload in
        <strong id="${FORCE_RELOAD_ID}-count">${FORCE_RELOAD_COUNTDOWN_S}</strong> seconds.
      </span>
    </div>
    <button id="${FORCE_RELOAD_ID}-btn" style="
      background: #d32f2f; color: white; border: none; padding: 8px 16px;
      border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 13px;
      width: 100%; text-align: center;
    ">Reload Now</button>
  `;

  document.body.appendChild(notice);

  document.getElementById(`${FORCE_RELOAD_ID}-btn`).addEventListener('click', () => {
    window.location.reload();
  });

  let remaining = FORCE_RELOAD_COUNTDOWN_S;
  const countEl = document.getElementById(`${FORCE_RELOAD_ID}-count`);
  const ticker = setInterval(() => {
    remaining -= 1;
    if (countEl) countEl.textContent = remaining;
    if (remaining <= 0) {
      clearInterval(ticker);
      window.location.reload();
    }
  }, 1000);
};

const showUpdateBanner = () => {
  if (document.getElementById(BANNER_ID)) return;

  const style = document.createElement('style');
  style.id = `${BANNER_ID}-style`;
  style.textContent = `
    @keyframes versionBannerSlideIn {
      from { transform: translateY(-100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);

  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.style.cssText = `
    position: fixed; top: 0; left: 0; right: 0; z-index: 999999;
    background: #004771; color: white; padding: 12px 20px;
    display: flex; align-items: center; justify-content: center; gap: 16px;
    font-family: 'Roboto', sans-serif; font-size: 14px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.25);
    animation: versionBannerSlideIn 0.3s ease-out;
  `;
  banner.innerHTML = `
    <span>System updates have occurred. Refresh to get the latest features and fixes.</span>
    <button id="${BANNER_ID}-refresh" style="
      background: #40B9C3; color: white; border: none; padding: 7px 18px;
      border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 13px;
      flex-shrink: 0;
    ">Refresh</button>
    <button id="${BANNER_ID}-dismiss" style="
      background: transparent; color: #ccc; border: none; cursor: pointer;
      font-size: 18px; padding: 0 4px; margin-left: 4px; flex-shrink: 0;
    " aria-label="Dismiss">&#x2715;</button>
  `;

  document.body.prepend(banner);

  document.getElementById(`${BANNER_ID}-refresh`).addEventListener('click', () => {
    window.location.reload();
  });
  document.getElementById(`${BANNER_ID}-dismiss`).addEventListener('click', () => {
    banner.remove();
    const s = document.getElementById(`${BANNER_ID}-style`);
    if (s) s.remove();
  });
};

const useVersionPolling = (isLoggedIn) => {
  const versionAtPageLoad = useRef(window.__APP_VERSION__ || '0.0.0');
  const timeoutRef = useRef(null);
  const remainingMsRef = useRef(POLL_INTERVAL_MS);
  const lastVisibleAtRef = useRef(Date.now());

  const schedulePoll = (delayMs) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    lastVisibleAtRef.current = Date.now();
    remainingMsRef.current = delayMs;
    timeoutRef.current = setTimeout(() => {
      poll();
      schedulePoll(POLL_INTERVAL_MS);
    }, delayMs);
  };

  const poll = async () => {
    try {
      const { minimumClientVersion, feVersion } = await fetchVersionInfo();

      if (minimumClientVersion && semverLt(versionAtPageLoad.current, minimumClientVersion)) {
        showForceReloadNotice();
        return;
      }

      const feVersionChanged = feVersion && feVersion !== versionAtPageLoad.current;

      if (feVersionChanged) {
        showUpdateBanner();
      }
    } catch {
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
          const elapsed = Date.now() - lastVisibleAtRef.current;
          remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
        }
      } else {
        schedulePoll(remainingMsRef.current);
      }
    };

    if (!isLoggedIn) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      remainingMsRef.current = POLL_INTERVAL_MS;
      return;
    }

    if (!document.hidden) {
      schedulePoll(POLL_INTERVAL_MS);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn]);
};

export default useVersionPolling;
